import RulaButton from '@pathccm/rula-ui/button';
import Text from '@/components/Text';
import Avatar from '@/components/Avatar';
import Icon from '@pathccm/rula-ui/icon';
import { useFlagCheck } from '@/utils/use-feature-flags';
import { AccountTypes } from '@/utils/types';
import AccountCardBadge from './AccountCardBadge';
import { ExpirationStatus } from './AccountCardBadge';
interface AccountCardProps {
  patientRecordUuid: string;
  patientUuid: string;
  isExpired?: boolean;
  isExpiring?: boolean;
  accountType?: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  onClick: (patientRecordUuid: string) => void;
  onCloseExpiredClick: (patientRecordUuid: string) => void;
  onTransferClick: (patientUuid: string, cta_type: string) => void;
  minorEmailNeeded?: boolean;
}

const AccountCard = ({
  patientRecordUuid,
  patientUuid,
  accountType,
  firstName,
  lastName,
  preferredName,
  onClick,
  onCloseExpiredClick,
  isExpired,
  isExpiring,
  minorEmailNeeded,
  onTransferClick,
}: AccountCardProps) => {
  const { isAllowed } = useFlagCheck();
  const graduatingMinorsEnabled = isAllowed({ releaseGraduatingMinors: true });
  let canRemoveAccount = isExpired;
  let expirationStatus = isExpired ? 'expired' : '';

  if (graduatingMinorsEnabled) {
    canRemoveAccount =
      !!(isExpired && accountType === AccountTypes.MinorDependent) &&
      !minorEmailNeeded;
    if (isExpiring) {
      expirationStatus = 'expiring';
    }
  }

  const showBadge = canRemoveAccount || expirationStatus;

  const handlePress = () => {
    if (graduatingMinorsEnabled) {
      if (isExpired && minorEmailNeeded) {
        onTransferClick(patientUuid, 'transfer_account_>18');
      } else {
        onClick(patientRecordUuid);
      }
    } else {
      onClick(patientRecordUuid);
    }
  };

  const getButtonText = () => {
    if (graduatingMinorsEnabled && isExpired && minorEmailNeeded) {
      return 'Transfer';
    }
    return 'Sign in';
  };

  const getRemoveButton = (classNames?: string) => {
    return (
      <div
        className={classNames || 'hidden md:flex'}
        role="button"
        onClick={() => onCloseExpiredClick(patientRecordUuid)}
        data-testid="remove-account"
      >
        <Icon icon="X" size="large" />
      </div>
    );
  };

  const getButton = () => {
    const button = canRemoveAccount ? (
      getRemoveButton()
    ) : (
      <RulaButton
        size="Medium"
        style="Secondary"
        onPress={() => handlePress()}
        className="w-full mt-4 md:w-[100px] md:mt-0"
      >
        {getButtonText()}
      </RulaButton>
    );
    return button;
  };

  const initials =
    (preferredName?.[0] || firstName?.[0] || '') + (lastName?.[0] || '');

  return (
    <div className="relative bg-grey-50 border-grey-200 border-1 rounded-[10px] w-[350px] flex flex-col px-6 py-5 items-center group md:justify-between md:flex-row md:w-[525px]">
      {isExpired &&
        getRemoveButton('absolute top-[22px] right-[22px] md:hidden')}
      <div className="flex flex-col items-center justify-center md:flex-row md:mb-0">
        <Avatar.Large initials={initials} />
        <div className="flex flex-col items-center md:items-start md:pl-3">
          <Text.P.Bold className="mt-2 text-overflow-ellipsis line-clamp-2 text-left md:mt-0">
            {preferredName || firstName} {lastName}
          </Text.P.Bold>
          {accountType && (
            <Text.P className="text-grey-800">{accountType}</Text.P>
          )}
        </div>
      </div>

      <div className="flex flex-col justify-center w-full flex-shrink-0 md:flex-row md:items-center md:w-auto">
        {showBadge && (
          <AccountCardBadge
            expirationStatus={expirationStatus as ExpirationStatus}
            haveEmail={!minorEmailNeeded}
            patientUuid={patientUuid}
            onTransferClick={onTransferClick}
          />
        )}
        {getButton()}
      </div>
    </div>
  );
};

export const NewAccountCard = ({
  onAddAccountClick,
}: {
  onAddAccountClick: () => void;
}) => {
  return (
    <div className="border-grey-200 border-1 rounded w-[350px] flex flex-col px-6 py-5 items-center bg-white md:justify-between md:flex-row md:w-[500px]">
      <div className="mb-4 flex flex-col items-center justify-center md:flex-row md:mb-0">
        <Avatar.Large />
        <div className="flex flex-col items-center md:items-start md:pl-3">
          <Text.P.Bold className="mt-2 text-overflow-ellipsis line-clamp-2 md:mt-0">
            Link a profile
          </Text.P.Bold>
        </div>
      </div>
      <RulaButton
        size="Medium"
        style="Secondary"
        onPress={onAddAccountClick}
        className="w-full md:w-[100px]"
      >
        Link
      </RulaButton>
    </div>
  );
};

export default AccountCard;
